import { deepQuerySelector } from 'theme';

// Reuse Prestige's filtering and lightbox: visible gallery cells are its source
// of truth. No cart/price/availability/variant ID is altered here.
const mappings = new WeakMap();
const selector = 'product-gallery[data-bh-variant-gallery]';

function mappingFor(gallery) {
  if (!mappings.has(gallery)) {
    try { mappings.set(gallery, JSON.parse(gallery.querySelector('[data-bh-variant-media-map]')?.textContent || '{}')); }
    catch { mappings.set(gallery, {}); }
  }
  return mappings.get(gallery);
}

function apply(gallery, variantId, selectHero = true) {
  const entry = mappingFor(gallery)[String(variantId)];
  if (!entry || !gallery.carousel) return null;
  const carousel = gallery.carousel;
  const cells = Array.from(carousel.children);
  const approved = new Set(entry.image_ids.map(String));
  const visible = cells.filter(cell => approved.has(cell.dataset.bhImageId));
  const empty = visible.length === 0;
  const filtered = [];
  cells.forEach((cell, index) => {
    const hide = cell.hasAttribute('data-bh-variant-placeholder') ? !empty : !approved.has(cell.dataset.bhImageId);
    if (hide) filtered.push(index);
  });
  gallery.dataset.bhVariantId = String(variantId);
  gallery.toggleAttribute('data-bh-empty', empty);
  gallery.setAttribute('filtered-indexes', JSON.stringify(filtered.map(index => index + 1)));
  const placeholderLabel = gallery.querySelector('[data-bh-placeholder-variant]');
  if (placeholderLabel) placeholderLabel.textContent = entry.title;
  // Filtering emits carousel:filter, so native thumbnail/dot navigation agrees.
  if (typeof carousel.filter === 'function') carousel.filter(filtered);
  else cells.forEach((cell,index) => cell.toggleAttribute('hidden', filtered.includes(index)));
  const hero = visible.find(cell => cell.dataset.bhImageId === String(entry.image_ids[0])) || visible[0];
  const target = hero || cells.find(cell => cell.hasAttribute('data-bh-variant-placeholder'));
  if (selectHero && target && typeof carousel.select === 'function') {
    const index = carousel.cells.indexOf(target);
    if (index >= 0) carousel.select(index, {instant:true});
  }
  visible.forEach((cell,index) => cell.setAttribute('aria-label', `Image ${index+1} of ${visible.length}`));
  gallery.dispatchEvent(new CustomEvent('bh:variant-media:change', {bubbles:true,detail:{formId:gallery.getAttribute('form'),variantId:String(variantId),mapping:entry}}));
  return hero ? {id:Number(hero.dataset.mediaId),position:Number(hero.dataset.mediaPosition),preview_image:entry.images[0]} : null;
}

function galleriesForForm(formId) {
  return Array.from(document.querySelectorAll(selector)).filter(gallery => gallery.getAttribute('form') === formId);
}

// product:rerender does not bubble, but capture receives it. Correct the active
// index before Prestige's own re-render listener filters a shorter image group.
document.addEventListener('product:rerender', event => {
  if (!event.detail?.htmlFragment) return;
  for (const gallery of galleriesForForm(event.target.getAttribute?.('id'))) {
    const replacement = deepQuerySelector(event.detail.htmlFragment, `${selector}[form="${CSS.escape(gallery.getAttribute('form'))}"]`);
    if (replacement) apply(gallery, replacement.dataset.bhVariantId);
  }
}, true);

document.addEventListener('variant:change', event => {
  const variant = event.detail?.variant;
  if (!variant?.id) return;
  const formId = event.target.getAttribute?.('id') || (typeof event.detail.formId === 'string' ? event.detail.formId : '');
  for (const gallery of galleriesForForm(formId)) {
    const hero = apply(gallery, variant.id);
    // Several source variants have the wrong featured photo assigned. Give the
    // native gallery a presentation-only copy with the verified media target.
    // Picker internals and purchase fields retain the original Shopify variant.
    event.detail.variant = {...event.detail.variant, featured_media:hero};
  }
}, true);

document.addEventListener('lightbox:open', event => {
  const gallery = event.target.closest?.(selector);
  if (gallery?.hasAttribute('data-bh-empty')) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

function initialize(scope=document) {
  scope.querySelectorAll(selector).forEach(gallery => apply(gallery, gallery.dataset.bhVariantId));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',()=>initialize(),{once:true});
else initialize();
document.addEventListener('shopify:section:load',event=>initialize(event.target));
